import { TypeAttribute } from '@trace/types';
import { DateTime } from 'luxon';

import { formatter1, formatterMs } from './metric/getFormattedValue';

type AttributeValue = string | number | null;

const stringPlaceholderValue = '-';

export const formatterDuration = (duration?: AttributeValue): string => {
  switch (typeof duration) {
    case 'string':
      return formatterMs(parseInt(duration, 10));
    case 'number':
      return formatterMs(duration);
    default:
      return stringPlaceholderValue;
  }
};

export const formatterTimestamp = (timestamp?: AttributeValue): string => {
  const destFormat = 'yyyy MMMM dd, HH:mm ZZZZ';

  switch (typeof timestamp) {
    case 'number':
      return DateTime.fromMillis(timestamp).toFormat(destFormat);
    case 'string':
      return DateTime.fromISO(timestamp).toFormat(destFormat);
    default:
      return stringPlaceholderValue;
  }
};

// Formatters that
const explicitFormatters: {
  [key in TypeAttribute]?: (v?: AttributeValue) => string;
} = {
  duration: formatterDuration,
  timestamp: formatterTimestamp,
};

/**
 * Formats a given Attribute value with
 *
 * @param  {TypeAttribute} attribute
 * @param  {null|number|string} value?
 * @returns string
 */
const getFormattedAttributeValue = (attribute: TypeAttribute, value?: AttributeValue): string => {
  const explicitFormatter = explicitFormatters[attribute];

  // For attributes that we want to handle specifically, use its formatter
  if (explicitFormatter) return explicitFormatter(value);

  // For string values, use that value as-is - or in the case of empty strings, a placeholder
  if (value === null || value === undefined || typeof value === 'string')
    return value || stringPlaceholderValue;

  // For numbers, format these in a human-readable way
  if (!Number.isNaN(value)) return formatter1(value);

  // For everything else, just stringify the value
  return value.toString();
};

export default getFormattedAttributeValue;
